import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-display uppercase text-xl sm:text-2xl text-white">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-white/65">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[color:var(--hs-bg-0)] text-white" data-testid="privacy-policy-page">
      <div className="noise-overlay" aria-hidden="true" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 relative z-10">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-xs text-white/50 hover:text-[color:var(--hs-lime)]" data-testid="privacy-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to holdstill.in
          </Link>
          <img src="/logo.png" alt="Holdstill Records" className="h-8 w-auto" />
        </div>

        <div className="mt-10 flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-[rgba(202,255,26,0.12)] border border-[rgba(202,255,26,0.3)] flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-[color:var(--hs-lime)]" />
          </span>
          <div>
            <h1 className="font-display uppercase text-3xl sm:text-4xl">Privacy Policy</h1>
            <p className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-white/40 mt-1">
              Version 1.0 · Compliant with the Digital Personal Data Protection Act, 2023 (India)
            </p>
          </div>
        </div>

        <p className="mt-6 text-sm leading-relaxed text-white/65">
          Holdstill Records (“we”, “us”) operates www.holdstill.in. We are a Data Fiduciary under the
          Digital Personal Data Protection Act, 2023 (“DPDP Act”). This notice explains — in plain
          language — what personal data we collect, why we collect it, how we protect it, and the
          rights you hold as a Data Principal.
        </p>

        <Section title="1. What we collect">
          <p>
            <span className="text-white/90 font-medium">Consultation form (only with your explicit consent):</span>{" "}
            name, email address, contact number, the type of music entity you represent, links to your public
            profiles/social pages, your preferred consultation date &amp; time, and any optional message you choose to share.
          </p>
          <p>
            <span className="text-white/90 font-medium">Usage data (only after you accept the consent banner):</span>{" "}
            page visited, referrer, ad-campaign parameters (UTM), and a random pseudonymous session key. We do not
            collect your IP address, precise location or device fingerprints for analytics.
          </p>
          <p>
            <span className="text-white/90 font-medium">Cookies:</span> a strictly-necessary session cookie is used only
            for Holdstill team members signing into the internal CRM. Your consent-banner choice is stored on your own
            device. No advertising cookies are set unless and until the Meta Pixel is activated, which also only runs
            after you accept the consent banner.
          </p>
        </Section>

        <Section title="2. Why we collect it (purpose & lawful basis)">
          <p>
            We process your personal data on the basis of your <span className="text-white/90 font-medium">free, specific,
            informed and unambiguous consent</span> (Section 6, DPDP Act), given via the checkbox on our consultation
            form and/or the consent banner. Purposes are limited to: (a) scheduling and conducting your free
            consultation; (b) contacting you about your enquiry; (c) understanding how visitors use the site so we can
            improve it; and (d) measuring ad-campaign performance where applicable.
          </p>
          <p>We do not use your data for any purpose beyond what you consented to, and we do not sell personal data.</p>
        </Section>

        <Section title="3. How it is stored & protected">
          <p>
            Data is stored in an access-controlled database and transmitted over encrypted (HTTPS/TLS) connections.
            Access is restricted to authorised Holdstill Records team members who sign in with verified Google accounts.
            We apply reasonable security safeguards as required by Section 8 of the DPDP Act to prevent personal data
            breach, and data minimisation is practised throughout — we only ask for what we actually need.
          </p>
        </Section>

        <Section title="4. Retention">
          <p>
            We retain consultation data only for as long as needed to serve your enquiry and our business relationship.
            If you request erasure, or when the data is no longer necessary for its stated purpose, it is deleted from
            our systems.
          </p>
        </Section>

        <Section title="5. Sharing & third parties">
          <p>
            Your data is not sold or rented. It may be processed by our infrastructure providers (secure cloud hosting)
            strictly on our instructions. If Meta advertising measurement is active and you have accepted the consent
            banner, anonymised conversion events (e.g. “a lead was submitted”) are shared with Meta Platforms — you can
            decline this entirely via the consent banner. Embedded Instagram content on our site is loaded from
            Instagram and is subject to Meta's own terms when you interact with it.
          </p>
        </Section>

        <Section title="6. Your rights as a Data Principal (Chapter III, DPDP Act)">
          <p>You have the right to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><span className="text-white/90">Access</span> — request a summary of the personal data we hold about you.</li>
            <li><span className="text-white/90">Correction &amp; erasure</span> — have inaccurate data corrected or your data deleted.</li>
            <li><span className="text-white/90">Withdraw consent</span> — at any time, as easily as it was given; we will stop processing on withdrawal.</li>
            <li><span className="text-white/90">Grievance redressal</span> — have your concerns addressed by us, and escalate to the Data Protection Board of India if unresolved.</li>
            <li><span className="text-white/90">Nominate</span> — appoint another individual to exercise your rights in case of death or incapacity.</li>
          </ul>
          <p>
            To exercise any right, email{" "}
            <a href="mailto:info@holdstill.in" className="text-[color:var(--hs-lime)] underline underline-offset-2">
              info@holdstill.in
            </a>{" "}
            with the subject “Data Request”. We respond to verified requests within a reasonable timeframe.
          </p>
        </Section>

        <Section title="7. Grievance Officer">
          <p>
            Grievance / Data Protection contact: Holdstill Records —{" "}
            <a href="mailto:info@holdstill.in" className="text-[color:var(--hs-lime)] underline underline-offset-2">
              info@holdstill.in
            </a>
            . If you are not satisfied with our response, you may approach the Data Protection Board of India under the
            DPDP Act, 2023.
          </p>
        </Section>

        <Section title="8. Children">
          <p>
            Our services are directed at artists and music businesses. We do not knowingly process personal data of
            children (under 18) without verifiable parental consent, as required by Section 9 of the DPDP Act.
          </p>
        </Section>

        <Section title="9. Changes to this policy">
          <p>
            We may update this policy as our services or the law evolve. Material changes will be reflected here with a
            new version number and, where required, fresh consent will be sought.
          </p>
        </Section>

        <p className="mt-10 pt-6 border-t border-white/10 font-mono-ui text-[11px] tracking-[0.2em] uppercase text-white/35">
          Holdstill Records · Music Growth Systems · info@holdstill.in
        </p>
      </div>
    </div>
  );
}
